/* eslint-disable no-alert */
/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import { saveAs } from 'file-saver';
import { Document, Packer } from 'docx';
import createTrack from 'services/actions/tracker_actions';
import { BidderFileGeneratedHandler } from 'services/actions/ai_management_files';
import IconAlert from 'assets/images/contract_analysis/icon-alert.svg';
import Icon2 from 'assets/images/contract_analysis/icon-2.svg';
import IconDoc from 'assets/images/contract_analysis/doc.svg';
import { ContractsAnalysisItemTypes } from 'store/modules/contract_analysis/types';
import DivergencesTable from 'components/molecules/divergences_table';
import MissingClauses from 'components/molecules/missing_clauses';
import { getManifestationTemplate } from './manifestationTemplates';
import { getDocxParagraphsFromEditor } from './docxUtils';
import { Container } from './styles';

interface ContractAnalysisReportProps {
  analysis: ContractsAnalysisItemTypes | null;
  title?: string;
  onFileGenerated?: BidderFileGeneratedHandler;
}

const ContractAnalysisReport: React.FC<ContractAnalysisReportProps> = ({
  analysis,
  title,
  onFileGenerated,
}) => {
  const printRef = useRef<HTMLDivElement>(null);
  const manifestationEditorRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<'divergencias' | 'clausulas' | 'sugestao'>(
    'divergencias',
  );

  useEffect(() => {
    if (!manifestationEditorRef.current || activeTab !== 'sugestao') return;

    manifestationEditorRef.current.innerHTML = getManifestationTemplate(
      analysis?.law,
      analysis?.bidding_number,
    );
  }, [activeTab, analysis?.law, analysis?.bidding_number]);

  const handleEditorCommand = (command: string, value?: string) => {
    if (!manifestationEditorRef.current) return;

    manifestationEditorRef.current.focus();
    document.execCommand(command, false, value);
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `relatorio-${analysis?.id}`,
  });

  const handleGenerateDocx = async () => {
    if (!manifestationEditorRef.current) return;

    try {
      const today = new Date().toISOString().slice(0, 10);
      const paragraphs = getDocxParagraphsFromEditor(manifestationEditorRef.current);

      if (!paragraphs.length) return;

      const doc = new Document({
        sections: [
          {
            properties: {
              page: {
                margin: {
                  top: 1440,
                  right: 1440,
                  bottom: 1440,
                  left: 1440,
                },
              },
            },
            children: paragraphs,
          },
        ],
      });

      const docBlob = await Packer.toBlob(doc);

      const filename = `manifestacao-${today}.docx`;
      saveAs(docBlob as Blob, filename);
      onFileGenerated?.('manifestacao_contratual', docBlob as Blob, filename);
      createTrack('Gerou docx da sugestão de manifestação', {
        fonte: 'Análise de contrato',
      });
    } catch (error) {
      createTrack('Falha ao gerar docx da sugestão de manifestação', {
        fonte: 'Análise de contrato',
      });
      alert('Não foi possível gerar o documento. Tente novamente.');
    }
  };

  return (
    <Container ref={printRef}>
      <div className="d-flex justify-content-between align-items-start">
        <div>
          {title && <h3 className="mb-1 font-weight-bold">{title}</h3>}

          <div className="text-muted">
            {analysis?.modality} {analysis?.bidding_number} - {analysis?.organization}
          </div>
        </div>
        <div className="d-flex">
          <button type="button" className="btn btn-primary" onClick={handlePrint}>
            Exportar PDF
          </button>
        </div>
      </div>
      <div className="mt-3 bg-light p-4 border rounded">
        <h5 className="mb-3">Resumo Executivo</h5>
        <div className="row">
          <div className="col-sm-4">
            <button
              type="button"
              className={`card border shadow-sm h-100 rounded card-hover text-left ${
                activeTab === 'divergencias' ? 'border-secondary' : ''
              }`}
              onClick={() => setActiveTab('divergencias')}
            >
              <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="mb-0">
                    {analysis?.analysis_result?.resumo_executivo?.total_divergencias}
                  </h2>
                  <div className="text-muted">Divergências Encontradas</div>
                </div>

                <img src={IconAlert} alt="icon" />
              </div>
            </button>
          </div>
          <div className="col-sm-4">
            <button
              type="button"
              className={`card border shadow-sm h-100 rounded card-hover text-left ${
                activeTab === 'clausulas' ? 'border-secondary' : ''
              }`}
              onClick={() => setActiveTab('clausulas')}
            >
              <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                  <h2 className="mb-0">
                    {analysis?.analysis_result?.resumo_executivo?.total_clausulas_ausentes}
                  </h2>
                  <div className="text-muted">Observações</div>
                </div>

                <img src={Icon2} alt="icon" />
              </div>
            </button>
          </div>
          <div className="col-sm-4">
            <button
              type="button"
              className={`card border shadow-sm h-100 rounded card-hover text-left ${
                activeTab === 'sugestao' ? 'border-secondary' : ''
              }`}
              onClick={() => setActiveTab('sugestao')}
            >
              <div className="card-body d-flex justify-content-between align-items-center">
                <div>
                  <div className="text-primary">Sugestão de Manifestação</div>
                </div>
                <img src={IconDoc} alt="icon" />
              </div>
            </button>
          </div>
        </div>
      </div>
      {activeTab !== 'sugestao' && (
        <ul className="nav nav-tabs custom-tabs">
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === 'divergencias' ? 'active' : ''}`}
              onClick={() => setActiveTab('divergencias')}
            >
              Divergências
            </button>
          </li>
          <li className="nav-item">
            <button
              type="button"
              className={`nav-link ${activeTab === 'clausulas' ? 'active' : ''}`}
              onClick={() => setActiveTab('clausulas')}
            >
              Observações
            </button>
          </li>
        </ul>
      )}
      {activeTab === 'clausulas' && (
        <div className="alert alert-warning mt-3 mb-0" role="alert">
          <strong>Aviso:</strong>{' '}
          {analysis?.law === 14133
            ? `Licitante, de acordo com o art. 92 da Lei nº 14.133/2021 (Nova Lei de Licitações e Contratos Administrativos), é necessário que o contrato contenha todas as cláusulas exigidas no referido dispositivo. No entanto, algumas delas não foram identificadas no instrumento analisado:`
            : `Licitante, de acordo com o art. 69 da Lei nº 13.303/2016 (Lei das Estatais), é necessário que o contrato contenha todas as cláusulas obrigatórias previstas no referido dispositivo. No entanto, algumas delas não foram identificadas no instrumento analisado:`}
        </div>
      )}
      <div className="py-3">
        {activeTab === 'sugestao' && (
          <div className="manifestation-wrapper d-print-none">
            <div className="manifestation-card">
              <h5 className="manifestation-heading">
                Edite o conteúdo abaixo para enviar ao órgão responsável e solicitar a manifestação
              </h5>

              <div className="manifestation-editor-shell">
                <div className="manifestation-toolbar">
                  <button
                    type="button"
                    className="toolbar-button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleEditorCommand('undo')}
                  >
                    <i className="material-icons">undo</i>
                  </button>
                  <button
                    type="button"
                    className="toolbar-button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleEditorCommand('redo')}
                  >
                    <i className="material-icons">redo</i>
                  </button>
                  <button
                    type="button"
                    className="toolbar-button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleEditorCommand('bold')}
                  >
                    <i className="material-icons">format_bold</i>
                  </button>
                  <button
                    type="button"
                    className="toolbar-button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleEditorCommand('italic')}
                  >
                    <i className="material-icons">format_italic</i>
                  </button>
                  <button
                    type="button"
                    className="toolbar-button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleEditorCommand('underline')}
                  >
                    <i className="material-icons">format_underlined</i>
                  </button>
                  <button
                    type="button"
                    className="toolbar-button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleEditorCommand('insertUnorderedList')}
                  >
                    <i className="material-icons">format_list_bulleted</i>
                  </button>
                  <button
                    type="button"
                    className="toolbar-button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleEditorCommand('insertOrderedList')}
                  >
                    <i className="material-icons">format_list_numbered</i>
                  </button>
                  <button
                    type="button"
                    className="toolbar-button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleEditorCommand('justifyLeft')}
                  >
                    <i className="material-icons">format_align_left</i>
                  </button>
                  <button
                    type="button"
                    className="toolbar-button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleEditorCommand('justifyCenter')}
                  >
                    <i className="material-icons">format_align_center</i>
                  </button>
                  <button
                    type="button"
                    className="toolbar-button"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => handleEditorCommand('justifyRight')}
                  >
                    <i className="material-icons">format_align_right</i>
                  </button>
                </div>

                <div
                  ref={manifestationEditorRef}
                  className="manifestation-editor"
                  contentEditable
                  suppressContentEditableWarning
                />
              </div>

              <div className="manifestation-actions">
                <button type="button" className="btn btn-primary" onClick={handleGenerateDocx}>
                  Gerar DOCX
                </button>
              </div>
            </div>
          </div>
        )}

        <div
          className={`tab-content-wrapper ${activeTab !== 'divergencias' ? 'd-none-print' : ''}`}
        >
          <DivergencesTable divergences={analysis?.analysis_result?.divergencias} />
        </div>

        <div className={`tab-content-wrapper ${activeTab !== 'clausulas' ? 'd-none-print' : ''}`}>
          <MissingClauses clauses={analysis?.analysis_result?.clausulas_ausentes} />
        </div>
      </div>
    </Container>
  );
};

export default ContractAnalysisReport;
